import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import api from "../api/axios";
import { clearOrderSession, CUSTOMER_SIGNED_OUT_EVENT } from "../utils/orderAccountSession";

export const SESSION_EXPIRED_EVENT = "sessionExpired";
const announceExpiry = () => window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));

const AuthContext = createContext();

// 7 days in milliseconds - must match backend JWT expiresIn: "7d"
const TOKEN_EXPIRY_TIME = 7 * 24 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem("locket_jwt");
    const storedExpiry = localStorage.getItem("locket_jwt_expiry");

    if (storedToken && storedExpiry) {
      const expiryTime = parseInt(storedExpiry, 10);
      const now = Date.now();
      if (now < expiryTime) {
        return storedToken;
      } else {
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
        return null;
      }
    }
    return null;
  });

  const [user, setUser] = useState(null);
  const expiryTimerRef = useRef(null);

  const isAuthenticated = !!token;

  useEffect(() => {
    const onAdminLogin = () => {
      setToken(null);
      setUser(null);
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
    // axios clears storage on 401 TOKEN_EXPIRED / TOKEN_REVOKED; drop the in-memory session too
    const onTokenExpired = () => {
      if (!localStorage.getItem("locket_jwt")) {
        setToken(null);
        setUser(null);
        announceExpiry();
      }
    };
    // A checkout-account login replaces the owner session
    const onCustomerSignedOut = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener("adminSessionActive", onAdminLogin);
    window.addEventListener("tokenExpired", onTokenExpired);
    window.addEventListener(CUSTOMER_SIGNED_OUT_EVENT, onCustomerSignedOut);
    return () => {
      window.removeEventListener("adminSessionActive", onAdminLogin);
      window.removeEventListener("tokenExpired", onTokenExpired);
      window.removeEventListener(CUSTOMER_SIGNED_OUT_EVENT, onCustomerSignedOut);
    };
  }, []);

  // Set up expiry timer when token changes
  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }

    if (token) {
      // Use the stored expiry (set by login()) rather than overriding it with a fresh 7 days.
      // This prevents token lifetime from silently extending on every app reload.
      const storedExpiry = parseInt(localStorage.getItem("locket_jwt_expiry") || "0", 10);
      const expiryTime = storedExpiry > Date.now() ? storedExpiry : Date.now() + TOKEN_EXPIRY_TIME;

      localStorage.setItem("locket_jwt", token);
      localStorage.setItem("locket_jwt_expiry", expiryTime.toString());

      const msLeft = expiryTime - Date.now();
      expiryTimerRef.current = setTimeout(() => {
        setToken(null);
        setUser(null);
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
        announceExpiry();
      }, Math.max(0, msLeft));
    } else {
      localStorage.removeItem("locket_jwt");
      localStorage.removeItem("locket_jwt_expiry");
      setUser(null);
    }

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }
    };
  }, [token]);

  const fetchUser = useCallback(async () => {
    if (!token) return null;
    try {
      const res = await api.get("/qr/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      return res.data;
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
        setToken(null);
        announceExpiry();
      }
      return null;
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchUser();
  }, [token, fetchUser]);

  const login = (jwt) => {
    localStorage.removeItem("admin_token");
    delete api.defaults.headers.common["Authorization"];
    clearOrderSession();
    window.dispatchEvent(new CustomEvent("customerSessionActive"));
    const expiryTime = Date.now() + TOKEN_EXPIRY_TIME;
    localStorage.setItem("locket_jwt", jwt);
    localStorage.setItem("locket_jwt_expiry", expiryTime.toString());
    setToken(jwt);
  };

  const loginWithPassword = async ({ phone, qrCodeId, password }) => {
    const res = await api.post("/qr/login", { phone, qrCodeId, password });
    if (res.data?.token) {
      login(res.data.token);
    }
    return res.data;
  };

  /** Signs out of both the owner session and any checkout-account session. */
  const logout = useCallback(({ skipServer = false } = {}) => {
    // Best-effort server-side token revocation (fire-and-forget)
    if (token && !skipServer) {
      api.post("/qr/logout").catch(() => {});
    }
    clearOrderSession();
    setToken(null);
    setUser(null);
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, user, isAuthenticated, login, logout, loginWithPassword, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
